import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Clock3, Compass, Search, SlidersHorizontal, X } from 'lucide-react'
import SearchBar from '../components/SearchBar'
import VideoaulaCard from '../components/VideoaulaCard'
import api, { getApiError } from '../services/api'

const filtros = [
  { label: 'Todas', value: 'todas' },
  { label: 'Teoria', value: 'teorico' },
  { label: 'Prática', value: 'pratico' },
  { label: 'Revisão', value: 'revisao_rapida' },
  { label: 'Exercícios', value: 'exercicios' }
]

const RECENT_SEARCHES_KEY = 'eduplex-recent-searches-v1'

const readRecentSearches = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]')
    return Array.isArray(stored) ? stored.slice(0, 6) : []
  } catch (_) {
    return []
  }
}

function BuscaPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get('q')?.trim() || ''
  const materiaId = searchParams.get('materia_id') || ''
  const filtroAtivo = searchParams.get('estilo') || 'todas'
  const [recentSearches, setRecentSearches] = useState(readRecentSearches)

  const materiasQuery = useQuery({
    queryKey: ['materias', 'explorar'],
    queryFn: async () => (await api.get('/materias')).data.materias
  })

  const searchQuery = useQuery({
    queryKey: ['search', query, filtroAtivo, materiaId],
    queryFn: async () => {
      const params = { q: query }
      if (filtroAtivo !== 'todas') params.estilo = filtroAtivo
      if (materiaId) params.materia_id = materiaId
      return (await api.get('/search', { params })).data
    },
    enabled: query.length >= 2
  })

  const materias = useMemo(() => materiasQuery.data || [], [materiasQuery.data])
  const data = searchQuery.data
  const temas = data?.temas || []
  const videoaulas = data?.videoaulas || []
  const materiaSelecionada = useMemo(
    () => data?.materia || materias.find(materia => materia.id === materiaId),
    [data?.materia, materiaId, materias]
  )

  useEffect(() => {
    if (query.length < 2 || !data || searchQuery.isLoading || searchQuery.isError) return

    const entry = {
      query,
      materiaId,
      materiaNome: data.materia?.nome || '',
      estilo: filtroAtivo,
      createdAt: Date.now()
    }
    const key = `${query.toLocaleLowerCase('pt-BR')}|${materiaId}|${filtroAtivo}`

    setRecentSearches(current => {
      const next = [entry, ...current.filter(item => (
        `${item.query.toLocaleLowerCase('pt-BR')}|${item.materiaId || ''}|${item.estilo || 'todas'}` !== key
      ))].slice(0, 6)
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next))
      } catch (_) {
        // A exploração continua disponível quando o navegador bloqueia o armazenamento.
      }
      return next
    })
  }, [data, filtroAtivo, materiaId, query, searchQuery.isError, searchQuery.isLoading])

  const updateFilter = (name, value, defaultValue = '') => {
    const next = new URLSearchParams(searchParams)
    if (!value || value === defaultValue) next.delete(name)
    else next.set(name, value)
    setSearchParams(next, { replace: true })
  }

  const clearFilters = () => {
    const next = new URLSearchParams()
    if (query) next.set('q', query)
    setSearchParams(next, { replace: true })
  }

  const openRecentSearch = item => {
    const next = new URLSearchParams({ q: item.query })
    if (item.materiaId) next.set('materia_id', item.materiaId)
    if (item.estilo && item.estilo !== 'todas') next.set('estilo', item.estilo)
    setSearchParams(next)
  }

  const clearRecentSearches = () => {
    setRecentSearches([])
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY)
    } catch (_) {
      // Nada precisa ser feito quando o armazenamento está indisponível.
    }
  }

  return (
    <div className="space-y-6">
      <header className="rounded-2xl border border-gray-100 bg-gray-50/60 p-5 dark:border-zinc-800 dark:bg-zinc-900/50 lg:p-7">
        <div className="mb-5 flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm dark:bg-zinc-800 dark:text-blue-300">
            <Compass size={22} />
          </span>
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-gray-400">Busca geral</p>
            <h1 className="mt-1 text-xl font-medium lg:text-2xl">Explore qualquer assunto</h1>
            <p className="mt-1 text-sm leading-6 text-gray-500">Pesquise em todas as matérias ou use os filtros para encontrar a aula certa.</p>
          </div>
        </div>
        <SearchBar initialValue={query} preserveParams placeholder="Ex.: equação do 1º grau com exercícios" />
      </header>

      <section className="card space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={16} className="text-gray-400" />
            <div><h2 className="text-sm font-medium">Refine sua exploração</h2><p className="text-xs text-gray-400">Escolha uma matéria e o tipo de aula.</p></div>
          </div>
          {(materiaId || filtroAtivo !== 'todas') && (
            <button type="button" onClick={clearFilters} className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-gray-500 hover:text-black dark:hover:text-white">
              <X size={13} /> Limpar
            </button>
          )}
        </div>

        <div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-400">Matéria</p>
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 scrollbar-hide sm:mx-0 sm:flex-wrap sm:px-0">
            <button type="button" onClick={() => updateFilter('materia_id', '')} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${!materiaId ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black' : 'border-gray-200 text-gray-600 hover:border-gray-400 dark:border-zinc-700 dark:text-zinc-300'}`}>Todas</button>
            {materias.map(materia => (
              <button key={materia.id} type="button" onClick={() => updateFilter('materia_id', materia.id)} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${materiaId === materia.id ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black' : 'border-gray-200 text-gray-600 hover:border-gray-400 dark:border-zinc-700 dark:text-zinc-300'}`}>
                {materia.nome}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-400">Tipo de aula</p>
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 scrollbar-hide sm:mx-0 sm:flex-wrap sm:px-0">
            {filtros.map(filtro => (
              <button key={filtro.value} type="button" onClick={() => updateFilter('estilo', filtro.value, 'todas')} className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${filtroAtivo === filtro.value ? 'bg-black text-white dark:bg-white dark:text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'}`}>
                {filtro.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {query.length < 2 ? (
        <section className="space-y-3">
          {recentSearches.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Clock3 size={16} className="text-gray-400" /><h2 className="text-sm font-medium">Pesquisas recentes</h2></div>
              <button type="button" onClick={clearRecentSearches} className="text-xs text-gray-500 hover:text-black dark:hover:text-white">Limpar histórico</button>
            </div>
          )}
          {recentSearches.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {recentSearches.map(item => {
                const estiloLabel = filtros.find(filtro => filtro.value === item.estilo)?.label
                return (
                  <button key={`${item.query}-${item.materiaId}-${item.estilo}`} type="button" onClick={() => openRecentSearch(item)} className="card text-left transition-colors hover:border-gray-300">
                    <p className="truncate text-sm font-medium">{item.query}</p>
                    <p className="mt-1 truncate text-xs text-gray-400">{[item.materiaNome || 'Todas as matérias', estiloLabel !== 'Todas' ? estiloLabel : ''].filter(Boolean).join(' · ')}</p>
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="card py-8 text-center">
              <Search size={24} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm font-medium">O que você quer aprender?</p>
              <p className="mt-1 text-xs text-gray-500">Digite um assunto acima ou escolha primeiro uma matéria.</p>
            </div>
          )}
        </section>
      ) : (
        <section className="space-y-4">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-base font-medium">Resultados para “{query}”</h2>
              <p className="mt-0.5 text-xs text-gray-400">{materiaSelecionada ? `Explorando somente ${materiaSelecionada.nome}.` : 'Explorando todas as matérias.'}</p>
            </div>
            {data && !searchQuery.isLoading && <span className="shrink-0 text-xs text-gray-400">{data.total} opções</span>}
          </div>

          {searchQuery.isLoading && <p className="card text-sm text-gray-500">Buscando as melhores videoaulas...</p>}
          {searchQuery.isError && <div className="card text-sm"><p className="text-red-600">{getApiError(searchQuery.error, 'A busca falhou.')}</p><button type="button" onClick={() => searchQuery.refetch()} className="btn-secondary mt-3">Tentar novamente</button></div>}

          {data?.youtube_erro && (
            <div className="card border-amber-200 bg-amber-50 text-sm dark:border-amber-900 dark:bg-amber-950/30">
              <p className="font-medium text-amber-900 dark:text-amber-100">Busca ao vivo temporariamente indisponível</p>
              <p className="mt-1 text-amber-700 dark:text-amber-300">{data.youtube_erro}</p>
            </div>
          )}

          {!searchQuery.isLoading && !searchQuery.isError && data && !data.youtube_erro && data.total === 0 && (
            <div className="card py-8 text-center"><p className="text-sm font-medium">Nenhum resultado encontrado</p><p className="mt-1 text-xs text-gray-500">Tente outro termo ou limpe os filtros.</p></div>
          )}

          {temas.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium">Temas relacionados</h3>
              <div className="grid gap-2 md:grid-cols-2">
                {temas.map(tema => (
                  <Link key={tema.id} to={`/tema/${tema.id}`} className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 hover:border-gray-300 dark:border-zinc-800 dark:hover:border-zinc-600">
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{tema.nome}</p><p className="text-xs text-gray-500">{tema.modulo?.materia?.nome} → {tema.modulo?.nome}</p></div>
                    <ChevronRight size={16} className="text-gray-400" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {videoaulas.length > 0 && (
            <div>
              <div className="mb-2 flex items-center justify-between"><h3 className="text-sm font-medium">Videoaulas ({videoaulas.length})</h3><span className="text-[10px] text-gray-400">Resultados ao vivo do YouTube</span></div>
              <div className="grid gap-3 xl:grid-cols-2">
                {videoaulas.map(video => <VideoaulaCard key={video.id} videoaula={video} contextLabel={data.materia?.nome} />)}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}

export default BuscaPage
